import { Box, Button, Link as MuiLink, Typography, useTheme } from "@mui/material";
import {
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import LogoLoader from "../../common/LogoLoader";
import { useMissions } from "../../../hooks/queries/useMissions";
import { useProductSearch } from "../../../hooks/queries/useProductSearch";
import { MissionProvider, Missions } from "../../../types/analytics";
import { ProductFilters } from "../../../types/productPlane";
import { ExportFacts } from "../../../utils/export/ExportFrame";
import { useCapturing } from "../../../utils/export/ExportStage";
import { toProductSearchParams } from "../../Explore/exploreState";
import { AnalyticsPaneProps } from "../analyticsPanes";
import {
  ANALYTICS_VIEW_LABELS,
  MissionsSettings,
  exploreAddress,
  selectionOf,
  toExploreState,
  toMissionsParams,
  withCentre,
} from "../analyticsState";
import { counted } from "../Ecosystem/EcosystemParts";
import MissionsControls from "./MissionsControls";
import { LABEL_WIDTH, MissionRing, MissionsLayout, layoutMissions } from "./missionsLayout";
import { CapabilityPanel, CategoryPanel, MissionsPick } from "./MissionsPanel";
import {
  BOX_SIZE,
  COULD_DASH,
  CapabilityFlowNode,
  CapabilityNode,
  CapabilityPill,
  CategoryBox,
  CategoryFlowNode,
  CategoryNode,
  CentreFlowNode,
  CentreNode,
  LineSample,
  MISSION_SIZE,
  MissionDot,
  MissionEdgeLine,
  MissionFlowEdge,
  MissionMark,
  MissionRingFlowNode,
  MissionRingNode,
  RingSample,
  centreName,
  edgeReading,
  lineColoursOf,
  markHeight,
  noProviderText,
  qualifierText,
} from "./MissionsParts";

type MissionsCanvasNode =
  | MissionRingFlowNode
  | CentreFlowNode
  | CapabilityFlowNode
  | CategoryFlowNode;

const NODE_TYPES = {
  ring: MissionRingNode,
  centre: CentreNode,
  capability: CapabilityNode,
  category: CategoryNode,
};
const EDGE_TYPES = { mission: MissionEdgeLine };
const FIT_VIEW_OPTIONS = { padding: 0.04, maxZoom: 1.15 };

// The keys of the canvas's nodes: a capability and a category may share no
// id, and the centre is one of neither
const CENTRE_KEY = "centre";
const capabilityKey = (id: string): string => `cap:${id}`;
const categoryKey = (id: string): string => `cat:${id}`;

// What each ring holds, in the corner of the canvas; a ring not drawn is
// not named
const RING_KEY: { ring: MissionRing; text: string }[] = [
  { ring: 1, text: "missions the hosts serve" },
  { ring: 2, text: "platform behaviours (and infrastructure)" },
  { ring: 3, text: "module and component capabilities (and those of unknown level)" },
  { ring: 4, text: "platforms, infrastructure and equipment that provide them" },
  { ring: 5, text: "modules, components, parts and software that provide them" },
];

// The capabilities with no provider line drawn, missions aside (they have
// none to draw): what each says of its providers
const unprovided = (
  layout: MissionsLayout,
  providers: readonly MissionProvider[],
): Map<string, string> => {
  const lined = new Set(
    layout.edges.filter((edge) => edge.kind === "provider").map((edge) => edge.capability),
  );
  return new Map(
    layout.capabilities
      .filter((placed) => placed.capability.level !== "operator" && !lined.has(placed.capability.id))
      .map((placed) => [placed.capability.id, noProviderText(providers)]),
  );
};

// The ring guides, the centre, the missions, the capabilities, the
// categories and the lines of a layout; the layout is never run again by a
// render, only by a new response
const MissionsCanvas: React.FC<{
  missions: Missions;
  layout: MissionsLayout;
  pick: MissionsPick | null;
  onPick: (pick: MissionsPick | null) => void;
  fitTick: number;
}> = ({ missions, layout, pick, onPick, fitTick }) => {
  const theme = useTheme();
  // The zoom buttons operate the canvas; an exported image has none
  const capturing = useCapturing();
  const [nodes, setNodes, onNodesChange] = useNodesState<MissionsCanvasNode>([]);
  const { fitView } = useReactFlow<MissionsCanvasNode>();
  const measured = useNodesInitialized();
  const centre = missions.centre;

  // What the selection lights: its own lines, and the nodes at their other
  // ends. A capability lights its spoke and its providers; a category its
  // lines and the capabilities they leave.
  const lit = useMemo(() => {
    const edges = new Set<string>();
    const nodeKeys = new Set<string>();
    if (pick) {
      for (const edge of layout.edges) {
        const touches =
          pick.kind === "capability"
            ? edge.capability === pick.id
            : edge.kind === "provider" && edge.category === pick.id;
        if (touches) {
          edges.add(edge.id);
          nodeKeys.add(capabilityKey(edge.capability));
          if (edge.category) {
            nodeKeys.add(categoryKey(edge.category));
          }
        }
      }
      nodeKeys.add(pick.kind === "capability" ? capabilityKey(pick.id) : categoryKey(pick.id));
    }
    return { edges, nodeKeys };
  }, [layout, pick]);

  useEffect(() => {
    if (!centre) {
      setNodes([]);
      return;
    }
    const guides: MissionRingFlowNode[] = layout.rings
      .filter((ring) => ring.held > 0)
      .map((ring) => ({
        id: `ring:${ring.ring}`,
        type: "ring",
        position: { x: -ring.rx, y: -ring.ry },
        data: { ring: ring.ring },
        style: { width: ring.rx * 2, height: ring.ry * 2 },
        zIndex: -1,
        draggable: false,
        selectable: false,
        focusable: false,
      }));
    const middle: CentreFlowNode = {
      id: CENTRE_KEY,
      type: "centre",
      position: { x: -LABEL_WIDTH / 2, y: -MISSION_SIZE / 2 },
      data: { centre },
      draggable: false,
      connectable: false,
      selectable: false,
    };
    // A node is placed by the centre of its mark; its name hangs beneath
    const quiet = unprovided(layout, missions.providers);
    const capabilities: CapabilityFlowNode[] = layout.capabilities.map((placed) => ({
      id: capabilityKey(placed.capability.id),
      type: "capability",
      position: {
        x: placed.x - LABEL_WIDTH / 2,
        y: placed.y - markHeight(placed.capability.level) / 2,
      },
      data: {
        capability: placed.capability,
        noProvider: quiet.get(placed.capability.id) ?? null,
        selected: false,
        dimmed: false,
        onSelect: () => onPick({ kind: "capability", id: placed.capability.id }),
      },
      draggable: false,
      connectable: false,
    }));
    const categories: CategoryFlowNode[] = layout.categories.map((placed) => ({
      id: categoryKey(placed.category.id),
      type: "category",
      position: { x: placed.x - LABEL_WIDTH / 2, y: placed.y - BOX_SIZE.height / 2 },
      data: {
        category: placed.category,
        selected: false,
        dimmed: false,
        onSelect: () => onPick({ kind: "category", id: placed.category.id }),
      },
      draggable: false,
      connectable: false,
    }));
    setNodes([...guides, middle, ...capabilities, ...categories]);
  }, [layout, centre, missions.providers, onPick, setNodes]);

  // The view is fitted once the names are measured, on every new layout
  // and on every press of Fit
  useEffect(() => {
    if (measured) {
      void fitView(FIT_VIEW_OPTIONS);
    }
  }, [measured, layout, fitTick, fitView]);

  // A change of selection lights and dims in place
  useEffect(() => {
    const pickedKey = pick
      ? pick.kind === "capability"
        ? capabilityKey(pick.id)
        : categoryKey(pick.id)
      : null;
    setNodes((current) =>
      current.map((node) => {
        if (node.type !== "capability" && node.type !== "category") {
          return node;
        }
        const selected = node.id === pickedKey;
        const dimmed = pick !== null && !lit.nodeKeys.has(node.id);
        const data = node.data as CapabilityFlowNode["data"];
        return data.selected === selected && data.dimmed === dimmed
          ? node
          : ({ ...node, data: { ...data, selected, dimmed } } as MissionsCanvasNode);
      }),
    );
  }, [layout, pick, lit, setNodes]);

  const edges = useMemo<MissionFlowEdge[]>(() => {
    const capabilities = new Map(missions.capabilities.map((entry) => [entry.id, entry]));
    const categories = new Map(missions.categories.map((entry) => [entry.id, entry]));
    const name = centre ? centreName(centre) : "";
    return layout.edges.map((placed) => {
      const active = lit.edges.has(placed.id);
      return {
        id: placed.id,
        type: "mission",
        source: placed.kind === "spoke" ? CENTRE_KEY : capabilityKey(placed.capability),
        target:
          placed.kind === "spoke"
            ? capabilityKey(placed.capability)
            : categoryKey(placed.category ?? ""),
        selectable: false,
        focusable: false,
        data: {
          kind: placed.kind,
          edge: placed.edge,
          capability: placed.capability,
          category: placed.category,
          x1: placed.x1,
          y1: placed.y1,
          x2: placed.x2,
          y2: placed.y2,
          width: placed.width,
          reading: edgeReading(
            placed.kind,
            placed.edge,
            capabilities.get(placed.capability),
            placed.category ? categories.get(placed.category) : undefined,
            name,
          ),
          active,
          dimmed: pick !== null && !active,
        },
      };
    });
  }, [layout, missions, centre, lit, pick]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      onPaneClick={() => onPick(null)}
      nodesDraggable={false}
      nodesConnectable={false}
      fitView
      fitViewOptions={FIT_VIEW_OPTIONS}
      minZoom={0.2}
      maxZoom={1.5}
      colorMode={theme.palette.mode}
      proOptions={{ hideAttribution: true }}
    >
      {!capturing && <Controls showInteractive={false} showFitView={false} />}
    </ReactFlow>
  );
};

// What each ring drawn holds, in the top-left corner over the canvas
const RingKey: React.FC<{ layout: MissionsLayout }> = ({ layout }) => (
  <Box
    aria-label="Ring key"
    sx={{
      position: "absolute",
      left: 18,
      top: 16,
      zIndex: 4,
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      pointerEvents: "none",
      fontSize: "10.5px",
      fontWeight: 500,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.disabled",
    }}
  >
    {RING_KEY.filter(({ ring }) => layout.rings.some((placed) => placed.ring === ring && placed.held > 0)).map(
      ({ ring, text }) => (
        <Box
          component="span"
          key={ring}
          data-ring={ring}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
        >
          <RingSample ring={ring} />
          {text}
        </Box>
      ),
    )}
    <Box
      component="span"
      sx={{ color: (theme) => theme.palette.chart.faintText, textTransform: "none", letterSpacing: 0 }}
    >
      ranked by share difference: how much more often the hosts claim it than comparable products
    </Box>
  </Box>
);

const LegendEntry: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="li" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
    {children}
  </Box>
);

// What the marks of the rings mean, under them, as the design writes it
const Legend: React.FC = () => {
  const theme = useTheme();
  const lines = lineColoursOf(theme.palette.chart);
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{
        m: 0,
        p: 0,
        listStyle: "none",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 2,
        rowGap: 1,
        fontSize: 12,
        color: "text.secondary",
      }}
    >
      <LegendEntry>
        <MissionMark sample />
        the centre
      </LegendEntry>
      <LegendEntry>
        <MissionDot size={12} />
        mission the hosts serve
      </LegendEntry>
      <LegendEntry>
        <CapabilityPill level="platform" sample /> platform behaviour
      </LegendEntry>
      <LegendEntry>
        <CapabilityPill level="module" sample />
        <CapabilityPill level="component" sample /> module · component capability
      </LegendEntry>
      <LegendEntry>
        <CategoryBox label="" inside endItem thin={false} sample />
        <CategoryBox label="" inside thin={false} sample />
        category on record inside the hosts · end item · fitted
      </LegendEntry>
      <LegendEntry>
        <CategoryBox label="" inside={false} thin={false} sample />
        category that could provide it
      </LegendEntry>
      <LegendEntry>
        <LineSample stroke={lines.inside} />
        on record
      </LegendEntry>
      <LegendEntry>
        <LineSample stroke={lines.could} dash={COULD_DASH} />
        could provide
      </LegendEntry>
      <LegendEntry>
        <LineSample stroke={lines.enables} />
        stated &quot;enables&quot;
      </LegendEntry>
      <LegendEntry>
        <LineSample stroke={theme.palette.accent.main} />
        selection
      </LegendEntry>
      <LegendEntry>
        <LineSample stroke={lines.spoke} />
        centre to capability, by lift
      </LegendEntry>
      <LegendEntry>× lift · hosts claiming it · a node under the floor is hatched</LegendEntry>
    </Box>
  );
};

// The rules the rings are drawn by, in sentences: who the hosts are, what
// the shares are measured against and ranked by, what the lines say, and
// what was left out
export const missionsCaption = (missions: Missions): string => {
  const centre = missions.centre;
  const name = centre ? centreName(centre) : "the centre";
  const qualifiers = centre ? qualifierText(centre).split(" · ").filter(Boolean) : [];
  const capabilities = (count: number) => `${count} ${count === 1 ? "capability" : "capabilities"}`;
  const categories = (count: number) => `${count} ${count === 1 ? "category" : "categories"}`;
  const left: string[] = [];
  if (missions.omitted_missions > 0) {
    left.push(
      `${counted(missions.omitted_missions, "mission")} past the ${missions.mission_limit} drawn`,
    );
  }
  if (missions.omitted_capabilities > 0) {
    left.push(`${capabilities(missions.omitted_capabilities)} past the ${missions.capability_limit} drawn`);
  }
  if (missions.omitted_categories > 0) {
    left.push(`${categories(missions.omitted_categories)} past the ${missions.category_limit} drawn`);
  }
  return [
    `The scope chooses the hosts: the ${counted(centre?.hosts ?? 0, "product")} of the scope that claim ${name}` +
      (qualifiers.length > 0 ? `, in a claim that also states ${qualifiers.join(" and ")}.` : "."),
    `Shares are against the ${counted(missions.baseline, "comparable product")}, the scope's products of the same categories with a claim; ` +
      "ranked by share difference, lift shown; it is co-occurrence, not cause.",
    'A solid line is a part on record inside the hosts, a dashed line a category that could provide, and teal a stated "enables"; a mission has no provider line.',
    `${capabilities(missions.thin_capabilities)} (missions among them) under the floor of ${counted(missions.floors.min_support, "host")} not drawn; ` +
      (left.length > 0 ? `left out by the bounds: ${left.join(", ")}.` : "nothing left out by the bounds."),
    ...(missions.thin_scope
      ? [`The scope holds under ${counted(missions.floors.min_scope, "product")}: every number is a hint.`]
      : []),
  ].join(" ");
};

const isNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404;

// The missions view: one capability at the centre, the missions its hosts
// serve and the capabilities that go with it on three rings, and the
// categories that provide them outside
const MissionsView: React.FC<AnalyticsPaneProps> = ({ state, onChange, onOpenEntity }) => {
  const settings = state.settings.missions;
  const params = useMemo(() => toMissionsParams(state), [state]);
  const read = useMissions(params);
  const missions: Missions | null = read.data ?? null;
  const layout = useMemo(() => (missions ? layoutMissions(missions) : null), [missions]);
  const [pick, setPick] = useState<MissionsPick | null>(null);
  const [fitTick, setFitTick] = useState(0);

  // The centre picker's capabilities are the scope's capability tree, as
  // the product search counts it: the request the page itself makes for its
  // scope bar, so that it is answered from the cache
  const searchParams = useMemo(() => toProductSearchParams(toExploreState(selectionOf(state))), [state]);
  const search = useProductSearch(searchParams);
  const filters: ProductFilters | null = search.data?.filters ?? null;
  const tree = filters?.choices.find((filter) => filter.name === "capability")?.values ?? null;

  const setSettings = useCallback(
    (next: MissionsSettings) => {
      setPick(null);
      onChange({ settings: { ...state.settings, missions: next } });
    },
    [onChange, state.settings],
  );
  const putAtCentre = useCallback(
    (id: string) => setSettings(withCentre(settings, id)),
    [setSettings, settings],
  );

  const capability =
    pick?.kind === "capability"
      ? (missions?.capabilities.find((entry) => entry.id === pick.id) ?? null)
      : null;
  const category =
    pick?.kind === "category"
      ? (missions?.categories.find((entry) => entry.id === pick.id) ?? null)
      : null;
  // A pick the response no longer holds is let go
  useEffect(() => {
    if (pick && missions && !capability && !category) {
      setPick(null);
    }
  }, [pick, missions, capability, category]);

  const centre = missions?.centre ?? null;
  const drawn = centre !== null && centre.hosts > 0 && layout !== null;
  // In an exported image: the centre said in words, in place of the
  // controls that set it, and no panel
  const capturing = useCapturing();

  const message = (children: React.ReactNode) => (
    <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3 }}>{children}</Box>
  );

  let body: React.ReactNode;
  if (!missions || !layout) {
    body = message(
      read.isLoading ? (
        <LogoLoader size="view" surface="paper" label="Loading the missions" />
      ) : isNotFound(read.error) ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>
            Centre not found: {[settings.centre, settings.condition, settings.mode].filter(Boolean).join(" · ")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSettings(withCentre(settings, null))}
            sx={{ textTransform: "none" }}
          >
            Clear centre
          </Button>
        </Box>
      ) : (
        <Typography color="text.secondary">The missions could not be loaded.</Typography>
      ),
    );
  } else if (!centre) {
    body = message(
      <Box sx={{ textAlign: "center", maxWidth: 520 }}>
        <Typography color="text.secondary" gutterBottom>
          No product in this scope claims a mission, the centre by default.
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Pick a capability for the centre above, or widen the scope:{" "}
          <MuiLink component={Link} to={exploreAddress(selectionOf(state))} underline="hover">
            change it in Explore
          </MuiLink>
          , or drop a filter.
        </Typography>
      </Box>,
    );
  } else if (centre.hosts === 0) {
    body = message(
      <Box sx={{ textAlign: "center", maxWidth: 520 }}>
        <Typography color="text.secondary" gutterBottom>
          No product in this scope claims {centreName(centre)}
          {qualifierText(centre) ? ` (${qualifierText(centre)})` : ""}.
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Pick another centre above, or a wider condition or mode, or widen the scope.
        </Typography>
      </Box>,
    );
  } else {
    body = (
      <>
        <RingKey layout={layout} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            // The surface shows through: no second ground under the rings
            "& .react-flow": { backgroundColor: "transparent" },
          }}
        >
          <ReactFlowProvider>
            <MissionsCanvas
              missions={missions}
              layout={layout}
              pick={pick}
              onPick={setPick}
              fitTick={fitTick}
            />
          </ReactFlowProvider>
        </Box>
        {capability && !capturing && (
          <CapabilityPanel
            missions={missions}
            capability={capability}
            selection={selectionOf(state)}
            onSelect={setPick}
            onCentre={putAtCentre}
            onOpenEntity={onOpenEntity}
          />
        )}
        {category && !capturing && (
          <CategoryPanel
            missions={missions}
            category={category}
            onSelect={setPick}
            onOpenEntity={onOpenEntity}
          />
        )}
      </>
    );
  }

  return (
    <Box
      component="section"
      aria-label={ANALYTICS_VIEW_LABELS.missions}
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.75 }}
    >
      {capturing ? (
        centre && (
          <ExportFacts
            aria-label="Centre"
            facts={[
              [
                "Centre",
                `${centreName(centre)}${qualifierText(centre) ? ` (${qualifierText(centre)})` : ""}`,
              ],
            ]}
          />
        )
      ) : (
        <MissionsControls
          settings={settings}
          missions={missions?.missions ?? []}
          conditions={missions?.conditions ?? []}
          modes={missions?.modes ?? []}
          tree={tree}
          treeLoading={search.isLoading}
          centre={centre}
          onChange={setSettings}
          drawn={drawn}
          onFit={() => setFitTick((tick) => tick + 1)}
        />
      )}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          minHeight: 640,
          border: 1,
          borderColor: "divider",
          borderRadius: (theme) => `${theme.radius.surface}px`,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        {body}
      </Box>
      {missions && centre && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {drawn && <Legend />}
          <Typography variant="caption" component="div" color="text.disabled" aria-label="How to read the rings">
            {missionsCaption(missions)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MissionsView;
